import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { SiteAuthProvider } from '@wix/cli-auth';
import { Spinner, Text, useAsync } from '@wix/cli-ui-kit';
import { createPackageManager, getRepoType } from '@wix/package-manager';
import { useProjectModel } from '../../model.js';
import { DevScreenBorder } from './DevScreenBorder.js';
import {
  runAstroDev,
  EVENT_LINE,
  EVENT_SERVER_ADDRESS,
} from './utils/runAstroDev.js';
import { createAstroLogger } from './utils/createAstroLogger.js';
import { DevCenterUpdater } from './DevCenterUpdater.js';

interface DevCommandProps {
  port?: number;
  allowedHosts?: string;
  serverExposedUrl?: string;
}

export const DevCommand: FC<DevCommandProps> = ({
  port,
  allowedHosts,
  serverExposedUrl,
}) => {
  const [serverLocalUrl, setServerLocalUrl] = useState('');

  const {
    model: {
      config: { siteId },
      projectFolder,
    },
  } = useProjectModel();

  const logger = useMemo(() => createAstroLogger(false), []);

  const astroProcess = useRef<ReturnType<typeof runAstroDev>>();

  useAsync(async () => {
    const repoType = await getRepoType(projectFolder);
    const packageManager = await createPackageManager(repoType);

    const astro = runAstroDev({
      packageManager,
      projectFolder,
      port,
      allowedHosts,
    });

    astroProcess.current = astro;

    astro.stdout?.once(EVENT_SERVER_ADDRESS, (address: string) => {
      setServerLocalUrl(address);
    });

    astro.stdout?.on(EVENT_LINE, (line: string) => logger.log(line));
    astro.stderr?.on(EVENT_LINE, (line: string) => logger.log(line));
  }, []);

  useEffect(() => {
    return () => {
      if (astroProcess.current?.pid) {
        try {
          astroProcess.current.kill('SIGTERM');
        } catch {
          // Sometimes child process might exit or be killed from outside.
          // We don't want to report an error in case we fail to kill the child process.
        }
      }
    };
  }, []);

  return (
    <SiteAuthProvider siteId={siteId}>
      {serverLocalUrl ? (
        <DevScreenBorder>
          <Text>Development environment ready</Text>
          <DevCenterUpdater
            serverLocalUrl={serverLocalUrl}
            serverExposedUrl={serverExposedUrl}
          />
        </DevScreenBorder>
      ) : (
        <Spinner text={'Starting dev environment...'} />
      )}
    </SiteAuthProvider>
  );
};
